import apiClient from '../api.js';

// Capa de servicios del módulo de congresos: coordinación (alta y edición del
// congreso), certificación (emisión e impresión) y caja (inscripciones).
// Backend en capas: `UNICEN/modules/congresos` (montado en /congresos). Este
// archivo refleja el contrato tal cual; no decide la forma de ningún payload.
//
// ── Sesión (contrato v2, auditoría de seguridad) ──────────────────────────────
//   TODOS los endpoints exigen `id_usuario` e `id_sede`: query en GET/DELETE,
//   body en POST/PATCH. Los completa ESTE archivo desde la sesión
//   (`sessionStorage.session`: `personal` = id_usuario, `id_sede`), así ninguna
//   pantalla puede olvidarlos ni mandar los de otra persona.
//   403 NO_AUTORIZADO si el usuario no tiene en esa sede el permiso de pantalla:
//     coordinación (crear/editar/planes/estado)  → `congresocood`
//     certificados (emitir, impresiones, firmas) → `congresocertificados`
//     caja (inscripciones)                       → `cajainscripcioncongreso`
//     GET /congresos y /:id/participantes        → cualquiera de los tres
//   Participantes: con `congresocertificados` se ven todos; coordinación ve sus
//   carreras (y las no asociadas si organiza). Lo decide el backend.
//
// Respuestas: `{ data }`. Fechas: 'AAAA-MM-DD'. Montos: números en bolivianos.
//
// ── Objeto Congreso ───────────────────────────────────────────────────────────
//   { id_item_seguimiento, descripcion, precio, activo: bool, id_gestion, id_sede,
//     horas, lugar, fecha_inicio, fecha_fin, id_plan_organizador,
//     planes: [{ id_congreso_plan_estudio, id_plan_estudio, carrera, organizador }],
//     conteos: { total, inscritos, pagados, becados, certificados, revisar,
//                certificados_emitidos,
//                inscritos_por_plan: [{ id_plan_estudio, inscritos }] },
//     mis_planes: [id_plan_estudio], participa, organiza, puede_editar }
//   `conteos.inscritos` son los que todavía DEBEN (estado INSCRITO), no el total.
//   `carrera` es el nombre completo de la carrera (no hay sigla).
//
// ── Coordinación ──────────────────────────────────────────────────────────────
//   GET    /congresos?id_gestion                              -> [Congreso]
//          Todos los congresos de la gestión y sede (activos e inactivos).
//   GET    /congresos/planes[?id_gestion]
//          -> [{ id_plan_estudio, carrera, id_carrera, tipo_plan,
//                congreso: { id_item_seguimiento, descripcion } | null }]
//          Planes que coordina el usuario y el congreso que ya tienen en la gestión.
//   POST   /congresos  (201)
//          body { descripcion, precio, id_gestion, fecha_inicio, fecha_fin?,
//                 horas, lugar?, planes: [ids], id_plan_organizador,
//                 activo? = true }                                -> Congreso
//          `fecha_inicio` y `horas` son OBLIGATORIOS al crear (400 si faltan).
//   PATCH  /congresos/:id  (atómico)
//          body { descripcion?, precio?, fecha_inicio?, fecha_fin? (null borra),
//                 horas?, lugar? (null borra), activo?,
//                 planes? (conjunto FINAL), id_plan_organizador? } -> Congreso
//          Un campo con el mismo valor que ya tiene no cuenta como cambio.
//          `fecha_inicio` y `horas` no se pueden borrar. La organizadora solo
//          cambia si se manda `id_plan_organizador`.
//   PATCH  /congresos/:id/estado  body { activo }              -> Congreso
//   GET    /congresos/:id/participantes[?id_plan_estudio]
//          -> { congreso, conteos, participantes: [Participante] }
//
//   Participante: { unicodigo, nombres, paterno, materno, nombre_completo,
//     id_plan_estudio, carrera, plan_asociado,
//     firmante: { id_plan_estudio, id_congreso_plan_estudio, carrera, nombre,
//                 cargo, es_organizadora, tiene_firma } | null,
//     monto (= precio), saldo, monto_descuento, total_pagado,
//     facturas: [{ numero_factura, fecha, monto }], inscripcion_duplicada,
//     estado: 'INSCRITO'|'PAGADO'|'BECADO'|'CERTIFICADO'|'REVISAR',
//     motivo_revision: 'SIN_FACTURA_NI_BECA'|'MONTO_INCONSISTENTE'|null,
//     certificado_sin_respaldo: bool,   // certificado vigente que hoy no cumple
//     elegible, reimprimible,
//     certificado: { id_certificado_valido, numero_factura, fecha_emision,
//                    impresiones, ultima_impresion } | null }
//   PAGADO exige facturado + descuento ≥ precio − 0,01; BECADO, descuento ≥
//   precio − 0,01; si no, REVISAR (MONTO_INCONSISTENTE).
//
// ── Certificación ─────────────────────────────────────────────────────────────
//   GET    /congresos/:id/firmas
//          -> { firmantes: [{ rol: 'DIRECTOR'|'COORDINADOR', id_plan_estudio,
//                             carrera, es_organizadora, nombre, cargo, url,
//                             tiene_firma, tiene_archivo }],
//               faltantes: [{ rol, id_plan_estudio, carrera,
//                             motivo: 'SIN_FIRMA'|'SIN_ARCHIVO' }] }
//          `url` es relativa (/uploads/...): se le antepone VUE_APP_API_URL.
//   POST   /congresos/:id/certificados  body { unicodigos }
//          -> { certificados: [CertificadoEmitido],
//               rechazados: [{ unicodigo, codigo: 'NO_ELEGIBLE', motivo,
//                              detalle?, estado?, nombre_completo? }],
//               resumen: { pedidos, emitidos, ya_emitidos, rechazados } }
//          Idempotente: los ya certificados vuelven con `nuevo: false` (mismo
//          número y QR). 409 EVENTO_INCOMPLETO sin fecha_inicio u horas. NO
//          bloquea por firma faltante: la pantalla verifica antes. Los rechazados
//          que el usuario no puede ver traen solo { unicodigo, codigo, motivo }.
//   POST   /congresos/certificados/impresiones  (201)
//          body { ids: [id_certificado_valido], fecha_impresion? }
//          -> { registradas, impresiones: [{ id_certificado_impresion,
//                                             id_certificado_valido, fecha_impresion }] }
//          `fecha_impresion` no puede ser futura (400); por defecto, hoy.
//
//   CertificadoEmitido: { id_certificado_valido, nuevo, unicodigo, nombres,
//     paterno, materno, nombre_completo, carrera, congreso, horas, lugar,
//     fecha_inicio, fecha_fin, numero_factura, fecha_emision,
//     firma_coordinador: { id_plan_estudio, carrera, es_organizadora, nombre,
//                          cargo, url, tiene_firma } | null,
//     firma_director: { nombre, cargo, url, tiene_firma } }
//
// ── Caja ──────────────────────────────────────────────────────────────────────
//   GET    /congresos/inscripciones?unicodigo&id_gestion
//          -> { estudiante: { unicodigo, nombres, paterno, materno, nombre_completo },
//               planes: [{ id_plan_estudio, carrera }],
//               inscripciones: [Inscripcion],
//               congresos_disponibles: { de_su_carrera: [Disponible],
//                                        otros: [Disponible] } }
//          404 NO_ENCONTRADO si el unicódigo no existe.
//   POST   /congresos/:id/inscripciones  (201)
//          body { unicodigo, id_plan_estudio }                -> Inscripcion
//          El monto NO se manda: sale del precio del congreso.
//   DELETE /congresos/inscripciones/:idDeuda
//          -> { id_deudas_contable, unicodigo, id_item_seguimiento, congreso,
//               eliminada: true }
//          404 si la deuda es de otra sede. 409 INSCRIPCION_CON_PAGOS (factura,
//          o saldo que bajó sin factura visible), INSCRIPCION_CON_BECA (tiene
//          descuento), INSCRIPCION_NO_PENDIENTE (estado de la deuda ≠ PENDIENTE),
//          INSCRIPCION_CON_CERTIFICADO.
//
//   Inscripcion: { id_deudas_contable, id_item_seguimiento, congreso,
//     id_plan_estudio, carrera, monto, monto_descuento, saldo, estado,
//     estado_deuda, facturas: [{ numero_factura, fecha, monto }],
//     certificado: { id_certificado_valido, fecha_emision } | null, duplicada }
//   Disponible: { id_item_seguimiento, descripcion, precio, horas, lugar,
//     fecha_inicio, fecha_fin, planes: [asociación], id_plan_estudio_sugerido }
//
// ── Errores ───────────────────────────────────────────────────────────────────
//   Forma: { codigo, mensaje }  (clave en español, NO `.message`).
//   400 DATOS_INVALIDOS · 403 NO_AUTORIZADO · 404 NO_ENCONTRADO · 500 ERROR_INTERNO
//   409: PRECIO_CON_INSCRITOS, NOMBRE_CON_CERTIFICADOS, PLAN_CON_INSCRITOS,
//        PLAN_ES_ORGANIZADOR, PLAN_CON_CONGRESO_EN_GESTION, NOMBRE_DUPLICADO,
//        YA_INSCRITO, CONGRESO_INACTIVO, INSCRIPCION_CON_PAGOS,
//        INSCRIPCION_CON_CERTIFICADO, INSCRIPCION_CON_BECA,
//        INSCRIPCION_NO_PENDIENTE, SIN_CARRERA_ESTUDIANTE, NO_ELEGIBLE,
//        PLAN_NO_ASOCIADO, CONGRESO_SIN_CARRERAS, CERTIFICADO_ANULADO,
//        EVENTO_INCOMPLETO.
//   La pantalla decide por `codigo`, nunca por el texto de `mensaje`.

const resource = '/congresos';

/**
 * `id_usuario` e `id_sede` de quien opera. Se lee de `sessionStorage` en cada
 * llamada (es lo que restaura App.vue al recargar) y no de una copia: si la
 * sesión cambia, la próxima llamada ya va con la nueva.
 */
export function sesionCongresos() {
  try {
    const sesion = JSON.parse(sessionStorage.getItem('session') || 'null');
    if (!sesion) return { id_usuario: null, id_sede: null };
    return { id_usuario: sesion.personal, id_sede: sesion.id_sede };
  } catch (e) {
    return { id_usuario: null, id_sede: null };
  }
}

/** Saca del query string las claves vacías: el backend valida `x=` como inválido. */
function limpiar(parametros) {
  const limpios = {};
  Object.keys(parametros || {}).forEach((clave) => {
    const valor = parametros[clave];
    if (valor !== null && valor !== undefined && valor !== '') limpios[clave] = valor;
  });
  return limpios;
}

/** Config de axios para GET/DELETE: la sesión va en el query. */
function conSesionEnQuery(parametros) {
  return { params: { ...limpiar(parametros), ...sesionCongresos() } };
}

/** Body de POST/PATCH con la sesión. La sesión pisa lo que venga: no se opera por otro. */
function conSesionEnBody(cuerpo) {
  return { ...(cuerpo || {}), ...sesionCongresos() };
}

const useCongresosApi = {
  // ── Coordinación ───────────────────────────────────────────────────────
  listarCongresos({ id_gestion }) {
    return apiClient.get(resource, conSesionEnQuery({ id_gestion }));
  },
  listarPlanesCoordinador({ id_gestion } = {}) {
    return apiClient.get(`${resource}/planes`, conSesionEnQuery({ id_gestion }));
  },
  crearCongreso(datos) {
    return apiClient.post(resource, conSesionEnBody(datos));
  },
  actualizarCongreso(idCongreso, cambios) {
    return apiClient.patch(`${resource}/${idCongreso}`, conSesionEnBody(cambios));
  },
  cambiarEstado(idCongreso, { activo }) {
    return apiClient.patch(`${resource}/${idCongreso}/estado`, conSesionEnBody({ activo }));
  },
  listarParticipantes(idCongreso, { id_plan_estudio } = {}) {
    return apiClient.get(`${resource}/${idCongreso}/participantes`, conSesionEnQuery({ id_plan_estudio }));
  },

  // ── Certificación ──────────────────────────────────────────────────────
  listarFirmas(idCongreso) {
    return apiClient.get(`${resource}/${idCongreso}/firmas`, conSesionEnQuery({}));
  },
  emitirCertificados(idCongreso, { unicodigos }) {
    return apiClient.post(`${resource}/${idCongreso}/certificados`, conSesionEnBody({ unicodigos }));
  },
  registrarImpresiones({ ids, fecha_impresion }) {
    return apiClient.post(`${resource}/certificados/impresiones`, conSesionEnBody({ ids, fecha_impresion }));
  },

  // ── Caja ───────────────────────────────────────────────────────────────
  consultarInscripciones({ unicodigo, id_gestion }) {
    return apiClient.get(`${resource}/inscripciones`, conSesionEnQuery({ unicodigo, id_gestion }));
  },
  inscribir(idCongreso, { unicodigo, id_plan_estudio }) {
    return apiClient.post(`${resource}/${idCongreso}/inscripciones`, conSesionEnBody({ unicodigo, id_plan_estudio }));
  },
  anularInscripcion(idDeuda) {
    return apiClient.delete(`${resource}/inscripciones/${idDeuda}`, conSesionEnQuery({}));
  },
};

// ── Helpers de error ─────────────────────────────────────────────────────────

/**
 * Lo que la pantalla necesita saber de un error de axios:
 *   - `status`: HTTP, o null si no hubo respuesta (sin conexión, CORS, caída);
 *   - `codigo`: el de máquina del backend, o null;
 *   - `mensaje`: el del backend para 4xx; para 500 y sin respuesta, null (el
 *     texto del 500 es genérico y la pantalla pone uno propio de la acción).
 */
export function errorDeCongresos(error) {
  const respuesta = error && error.response;
  if (!respuesta) {
    return { status: null, codigo: null, mensaje: null, sinConexion: true };
  }
  const datos = respuesta.data || {};
  const status = respuesta.status;
  return {
    status,
    codigo: datos.codigo || null,
    mensaje: status >= 500 ? null : datos.mensaje || null,
    sinConexion: false,
  };
}

/** 403 NO_AUTORIZADO: el usuario no tiene el permiso de pantalla en esta sede. */
export function esSinPermiso(error) {
  return errorDeCongresos(error).status === 403;
}

/**
 * Texto para mostrar: el `mensaje` del backend si lo hay; si no, uno que diga
 * qué acción falló y cómo seguir. Nunca "Error 500".
 */
export function mensajeDeError(error, textoDeLaAccion) {
  const info = errorDeCongresos(error);
  if (info.sinConexion) {
    return `${textoDeLaAccion}: no hay conexión con el servidor. Revise su conexión e intente nuevamente.`;
  }
  if (info.mensaje) return info.mensaje;
  return `${textoDeLaAccion}. Intente nuevamente; si el problema continúa, avise a ASI.`;
}

export default useCongresosApi;
